import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import { breakpoint, calcRem } from '../../__styling'

export const PollContainer = styled.div`
    position: relative;
    background: ${tokens.sevennews.colors.palette.white};
    border-radius: ${calcRem(12)};
    padding: ${calcRem(14, 16)};
    border: ${calcRem(4)} solid ${tokens.sevennews.colors.palette.red};
    max-width: ${calcRem(600)};
    margin: 0 auto ${calcRem(16)};

    ${breakpoint('sm')} {
        border-radius: ${calcRem(15)};
        padding: ${calcRem(20, 40)};
        border-width: ${calcRem(6)};
        margin: 0 auto ${calcRem(24)};
    }
`

export const PollLogo = styled.img`
    position: absolute;
    top: ${calcRem(12)};
    right: ${calcRem(14)};
    height: ${calcRem(24)};
    width: auto;

    ${breakpoint('sm')} {
        top: ${calcRem(20)};
        right: ${calcRem(26)};
        height: ${calcRem(32)};
    }
`

export const PollQuestion = styled.h3`
    font-size: ${calcRem(24)};
    font-weight: 700;
    color: ${tokens.sevennews.colors.palette.red};
    margin: 0 0 ${calcRem(16)} 0;
    padding-top: ${calcRem(24)};
    line-height: 1.25;

    ${breakpoint('xxs')} {
        font-size: ${calcRem(28)};
    }

    ${breakpoint('sm')} {
        font-size: ${calcRem(42)};
        margin-bottom: ${calcRem(24)};
        padding-top: ${calcRem(32)};
        line-height: 1.4;
    }
`

export const OptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${calcRem(8)};
    margin-bottom: ${calcRem(12)};

    ${breakpoint('sm')} {
        gap: ${calcRem(12)};
        margin-bottom: ${calcRem(16)};
    }
`

export const OptionLabel = styled.label<{ selected: boolean }>`
    display: flex;
    align-items: center;
    padding: ${calcRem(8)};
    border: ${calcRem(2)} solid ${tokens.sevennews.colors.palette.red};
    border-radius: ${calcRem(8)};
    background: ${(props) =>
        props.selected
            ? tokens.sevennews.colors.palette.red
            : tokens.sevennews.colors.palette.white};
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;

    &::before {
        content: '';
        display: inline-block;
        width: ${calcRem(22)};
        height: ${calcRem(22)};
        border: ${calcRem(2)} solid ${tokens.sevennews.colors.palette.red};
        border-radius: 50%;
        background: ${tokens.sevennews.colors.palette.white};
        transition: all 0.2s ease;
        flex-shrink: 0;
        position: relative;
    }

    &::after {
        content: '✓';
        position: absolute;
        left: ${calcRem(12)};
        top: 50%;
        transform: translateY(-50%);
        color: ${tokens.sevennews.colors.palette.red};
        font-family: system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica,
            Arial, sans-serif;
        font-size: ${calcRem(16)};
        font-weight: bold;
        opacity: ${(props) => (props.selected ? '1' : '0')};
        transition: opacity 0.2s ease;
    }

    &:focus-visible,
    &:focus-within,
    &:has(input:focus-visible) {
        outline: ${calcRem(3)} solid ${tokens.sevennews.colors.palette.black};
        outline-offset: ${calcRem(2)};
    }

    ${breakpoint('sm')} {
        padding: ${calcRem(10)};
        border-radius: ${calcRem(10)};

        &::before {
            width: ${calcRem(28)};
            height: ${calcRem(28)};
        }

        &::after {
            left: ${calcRem(15)};
            font-size: ${calcRem(20)};
        }
    }
`

export const OptionInput = styled.input`
    position: absolute;
    inset: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    cursor: pointer;
`

export const OptionText = styled.span<{ selected: boolean }>`
    font-size: ${calcRem(16)};
    font-weight: 800;
    color: ${(props) =>
        props.selected
            ? tokens.sevennews.colors.palette.white
            : tokens.sevennews.colors.palette.red};
    flex: 1;
    margin-left: ${calcRem(12)};

    ${breakpoint('sm')} {
        font-size: ${calcRem(18)};
        margin-left: ${calcRem(18)};
    }
`

export const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: ${calcRem(100)};
`

export const SubmitButton = styled.button`
    width: 100%;
    margin-top: ${calcRem(8)};
    padding: ${calcRem(12, 18)};
    background: ${tokens.sevennews.colors.palette.red};
    color: ${tokens.sevennews.colors.palette.white};
    font-size: ${calcRem(18)};
    font-weight: 700;
    border: ${calcRem(2)} solid ${tokens.sevennews.colors.palette.red};
    border-radius: ${calcRem(22)};
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover:not(:disabled) {
        background: ${tokens.sevennews.colors.palette.red};
    }

    &:disabled {
        color: ${tokens.sevennews.colors.palette.greyMarble};
        border: ${calcRem(1)} solid
            ${tokens.sevennews.colors.palette.greyMarble};
        background: ${tokens.sevennews.colors.palette.greyLight};
        cursor: not-allowed;
    }

    ${breakpoint('xxs')} {
        font-size: ${calcRem(20)};
    }

    ${breakpoint('sm')} {
        margin-top: ${calcRem(4)};
        padding: ${calcRem(16, 24)};
        font-size: ${calcRem(24)};
        border-radius: ${calcRem(28)};
    }
`

export const ResultsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${calcRem(8)};
`

export const ResultItem = styled.div<{ isSelected: boolean }>`
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0 ${calcRem(10)} 0
        ${(props) => (props.isSelected ? calcRem(36) : calcRem(8))};
    z-index: 1;

    &::before {
        content: '✓';
        position: absolute;
        left: ${calcRem(8)};
        top: 50%;
        transform: translateY(-50%);
        width: ${calcRem(22)};
        height: ${calcRem(22)};
        border-radius: 50%;
        background: ${tokens.sevennews.colors.palette.white};
        color: ${tokens.sevennews.colors.palette.red};
        border: ${calcRem(2)} solid ${tokens.sevennews.colors.palette.red};
        display: ${(props) => (props.isSelected ? 'flex' : 'none')};
        align-items: center;
        justify-content: center;
        font-family: system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica,
            Arial, sans-serif;
        font-size: ${calcRem(16)};
        font-weight: bold;
    }

    ${breakpoint('sm')} {
        padding: 0 ${calcRem(12)} 0
            ${(props) => (props.isSelected ? calcRem(44) : calcRem(10))};

        &::before {
            left: ${calcRem(10)};
            width: ${calcRem(28)};
            height: ${calcRem(28)};
            font-size: ${calcRem(20)};
        }
    }
`

export const ResultText = styled.span`
    font-size: ${calcRem(14)};
    color: ${tokens.sevennews.colors.palette.bauhausBlack};
    font-weight: 600;

    ${breakpoint('sm')} {
        font-size: ${calcRem(16)};
    }
`

export const ResultPercentage = styled.span`
    font-size: ${calcRem(14)};
    font-weight: 700;
    color: ${tokens.sevennews.colors.palette.black};

    ${breakpoint('sm')} {
        font-size: ${calcRem(16)};
    }
`

export const ResultBar = styled.div<{ isSelected: boolean }>`
    width: 100%;
    height: ${calcRem(44)};
    background: ${tokens.sevennews.colors.palette.white};
    border-radius: ${calcRem(9)};
    overflow: hidden;
    margin-bottom: ${calcRem(4)};
    position: relative;
    border: ${calcRem(1)} solid ${tokens.sevennews.colors.palette.red};
    box-shadow: 0 ${calcRem(2)} ${calcRem(8)} rgba(0, 0, 0, 0.1);
    ${(props) =>
        props.isSelected &&
        `
        border-color: ${tokens.sevennews.colors.palette.red};
    `}

    ${breakpoint('sm')} {
        height: ${calcRem(52)};
        border-radius: ${calcRem(11)};
        margin-bottom: ${calcRem(6)};
    }
`

export const ResultBarFill = styled.div<{ width: number }>`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    border-radius: ${calcRem(10)};
    width: ${(props) => props.width}%;
    background: ${tokens.sevennews.colors.palette.red};
    animation: growWidth 1s ease-out;

    @keyframes growWidth {
        from {
            width: 0%;
        }
    }
`

export const ResultBarContent = styled.div<{ isSelected: boolean }>`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 ${calcRem(12)};
    box-sizing: border-box;
    color: ${(props) =>
        props.isSelected
            ? tokens.sevennews.colors.palette.black
            : tokens.sevennews.colors.palette.greyMarble};

    ${ResultText},
    ${ResultPercentage} {
        color: inherit;
    }

    ${breakpoint('sm')} {
        padding: 0 ${calcRem(16)};
    }
`

export const TotalVotes = styled.div`
    text-align: center;
    margin-top: ${calcRem(8)};
    font-size: ${calcRem(18)};
    color: ${tokens.sevennews.colors.palette.black};
    font-weight: 600;

    ${breakpoint('sm')} {
        font-size: ${calcRem(24)};
    }
`

export const ResponseStatus = styled.div`
    text-align: center;
    margin-top: ${calcRem(8)};
    font-size: ${calcRem(18)};
    color: ${tokens.sevennews.colors.palette.red};
    font-weight: 600;

    ${breakpoint('sm')} {
        font-size: ${calcRem(24)};
    }
`

export const VoteErrorMessage = styled.div`
    text-align: center;
    margin-top: ${calcRem(8)};
    font-size: ${calcRem(16)};
    color: ${tokens.sevennews.colors.palette.red};
    font-weight: 600;

    ${breakpoint('sm')} {
        font-size: ${calcRem(18)};
    }
`

export const ShareLinkContainer = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${calcRem(6)};
    margin-top: ${calcRem(12)};
    margin-bottom: ${calcRem(12)};
    font-size: ${calcRem(16)};
    color: ${tokens.sevennews.colors.palette.black};
    font-weight: 600;
    cursor: pointer;
    background: none;
    border: none;
    width: 100%;
    padding: 0;

    &:hover {
        color: ${tokens.sevennews.colors.palette.red};
    }

    svg {
        width: ${calcRem(18)};
        height: ${calcRem(18)};
    }

    ${breakpoint('sm')} {
        gap: ${calcRem(8)};
        margin-top: ${calcRem(16)};
        margin-bottom: ${calcRem(16)};
        font-size: ${calcRem(18)};

        svg {
            width: ${calcRem(20)};
            height: ${calcRem(20)};
        }
    }
`
